import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { cn } from "@/lib/utils";
import { useSiteSettings, type ContactInfo } from "@/hooks/useSiteSettings";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DynamicIcon from "./DynamicIcon";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: contact } = useSiteSettings<ContactInfo>("contact");
  const phone = contact?.phone || "+33 4 93 00 00 00";
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!isHome) {
      e.preventDefault();
      navigate("/" + href);
    }
    setIsMenuOpen(false);
  };

  const { data: socialLinks } = useQuery({
    queryKey: ["public-social"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_links")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data.filter((l) => l.url && l.url.trim() !== "");
    },
    staleTime: 1000 * 60 * 5,
  });

  const navLinks = [
    { href: "#plage", label: t("nav.beach") },
    { href: "#restaurant", label: t("nav.restaurant") },
    { href: "#carte", label: t("nav.menu") },
    { href: "#evenements", label: t("nav.events") },
    { href: "#actualites", label: t("nav.news") },
    { href: "#contact", label: t("nav.contact") },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="flex items-center gap-2">
            <Logo size="sm" variant="color" loading="eager" fetchPriority="high" />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={isHome ? link.href : `/${link.href}`}
                className="font-display text-sm tracking-wider uppercase text-foreground hover:text-sage transition-colors duration-300"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <div className="flex items-center gap-3 text-foreground">
              {socialLinks?.map((link) => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-sage transition-colors" aria-label={link.platform}>
                  <DynamicIcon name={link.icon} className="w-5 h-5" />
                </a>
              ))}
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-sage transition-colors" aria-label={t("nav.phone")}>
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>

          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={t("nav.toggleMenu")}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "lg:hidden absolute top-full left-0 right-0 glass-sage overflow-hidden transition-all duration-300",
          isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <nav className="container mx-auto px-4 py-6">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={isHome ? link.href : `/${link.href}`}
                className="font-display text-lg tracking-wider uppercase text-primary-foreground hover:text-rose-light transition-colors py-2"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 border-t border-primary-foreground/20">
              <LanguageSwitcher />
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
